/**
 * building and publishing a request's merged document.
 */
import { fetchGraphQLApi } from '@helpers/callApi';
import { MERGED_AUDIT_LOG_ASSET_TYPE } from './mergedAuditLog';
import { TDOId } from '@common-modules/universal/models/Brands';
import {
  parseLatestCombinedVersion,
  parseMergedFilenameOptions,
  type MergedFilenameOptions,
} from './models';
import { isMachineWrittenRedactionAsset } from '@redact-modules/documents/redactionAssetWriter';
import type { ProjectBatesStamp } from './batesStamp';
import {
  parseProjectRedactionAssetDetails,
  PROJECT_REDACTION_ASSET_TYPE,
} from './projectRedactionAsset';
import {
  readAssetHasNoRedactions,
  readAssetIncludedUnreviewed,
} from '../documents/helpers';

/**
 * The merged PDF's asset type — THE SAME ONE a single file's output wears.
 *
 * It had a type of its own, `redacted-document-combined`, on the reasoning
 * that a distinct type keeps the merged PDF out of the queries that gather a
 * file's outputs. It does not, and it was not needed to:
 *
 * - `redacted-media-clip` IS a necessary split, and for a reason this case
 *   does not share: a clip and a full output sit on the SAME TDO, so a query
 *   there has to say which it wants. A project TDO has no sibling redacted
 *   document to be told apart from.
 * - Every read of it is addressed to the project TDO by id — see the queries
 *   below — so the type was filtering a set of one.
 * - The asset's own `details` already identify it beyond doubt, carrying
 *   `projectTdoId`, the `members` it was built from and what was `excluded`.
 *
 * And the direction of travel argues against it. A project TDO is a synthetic
 * file; give it a merged ORIGINAL alongside this and it has exactly a file's
 * shape — an original and a redacted output — at which point a bespoke type
 * is friction against reusing the machinery a file already has. The merged
 * row's staleness, its pill and its download gate are shared with its members
 * today for the same reason.
 *
 * What identifies the merged PDF is the TDO it is on, which is the fact that
 * is actually true of it.
 */
export const MERGED_DOCUMENT_ASSET_TYPE = 'redacted-document';

/** One member's latest redacted output, as the combine needs it. */
export interface MemberRedactedOutput {
  readonly tdoId: TDOId;
  /** The member's own filename, recorded so a release can be read back. */
  readonly name: string | null;
  readonly assetId: string;
  readonly signedUri: string | null;
  /** That member's redacted version — half of how staleness is judged. */
  readonly version: number;
  readonly numPages: number | null;
  /**
   * Did this member's burn-in remove anything?
   *
   * A MEASUREMENT. Read it with `includedUnreviewed` beside it — an empty
   * output and an unread document are the same measurement and opposite
   * claims. See `readOutputRedactionVerdict`.
   */
  readonly hasNoRedactions: boolean | null;
  /**
   * Was this member released WITHOUT anybody reviewing it (VE-32440)?
   *
   * Carried so the merged release cannot be named `[NO_REDACTIONS]` over a
   * document nobody has read — the one claim in this product that leaves the
   * organisation.
   */
  readonly includedUnreviewed: boolean;
}

interface AssetRecord {
  id: string;
  name: string | null;
  signedUri: string | null;
  details?: { version?: number; numPages?: number } | null;
}

/**
 * The newest `redacted-document` on each member, in the order given.
 */
export async function serviceResolveMemberOutputs(
  memberTdoIds: readonly TDOId[],
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{
  outputs: MemberRedactedOutput[];
  missing: TDOId[];
  /**
   * Members that have SAVED REDACTION DATA but no output — somebody worked on
   * them and never burned them in.
   */
  workedOnWithoutOutput: TDOId[];
}> {
  const query = `
    query memberRedactedOutput($id: ID!) {
      temporalDataObject(id: $id) {
        id
        name
        assets(assetType: "redacted-document", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records {
            id
            name
            signedUri
            details
          }
        }
        # Evidence that somebody has worked on this file, whatever the
        # client's snapshot says.
        #
        # details IS SELECTED because the existence of an asset is not the
        # question. The lexicon step in the upload dialog writes a
        # document-redaction-data asset before anybody opens the file, and
        # counting that as work made this refuse to merge over a document
        # nobody had ever looked at: "1 file has not been redacted yet",
        # clearable only by redacting a file the reviewer never meant to
        # touch. isMachineWrittenRedactionAsset is the same test the editor's
        # open path and watermarkAutoApply use; all three have to agree or
        # they are not talking about the same file.
        #
        # More than one record, because the newest asset may be the upload
        # stub even when a real save sits behind it.
        redactionData: assets(assetType: ["redaction-media-data", "document-redaction-data"], limit: 10) {
          records {
            id
            details
          }
        }
      }
    }
  `;
  const settled = await Promise.all(
    memberTdoIds.map(async (tdoId) => {
      const result = await fetchGraphQLApi<{
        temporalDataObject?: {
          id: TDOId;
          name: string | null;
          assets?: { records?: AssetRecord[] | null } | null;
          redactionData?: {
            records?: { id: string; details?: unknown }[] | null;
          } | null;
        } | null;
      }>({
        endpoint: `${apiRoot}/${graphQLEndpoint}`,
        query,
        variables: { id: tdoId },
        token,
        veritoneAppId,
      });
      if (result.errors?.length) {
        const firstErr = String(result.errors[0]?.message ?? 'unknown error');
        throw new Error(`merged document: member read failed: ${firstErr}`);
      }
      const tdo = result.data?.temporalDataObject;
      const asset = tdo?.assets?.records?.[0];
      const workedOn = (tdo?.redactionData?.records ?? []).some(
        (record) => !isMachineWrittenRedactionAsset(record.details)
      );
      if (!asset) {
        return { tdoId, workedOn, output: null as MemberRedactedOutput | null };
      }
      return {
        tdoId,
        workedOn,
        output: {
          tdoId,
          // The member's own name, not the asset's: the asset name carries
          // `[REDACTED].v3`, which is noise in a record of what went in.
          name: tdo?.name ?? asset.name,
          assetId: asset.id,
          signedUri: asset.signedUri,
          version:
            typeof asset.details?.version === 'number'
              ? asset.details.version
              : 0,
          numPages:
            typeof asset.details?.numPages === 'number'
              ? asset.details.numPages
              : null,
          hasNoRedactions: readAssetHasNoRedactions(asset.details),
          includedUnreviewed: readAssetIncludedUnreviewed(asset.details),
        } satisfies MemberRedactedOutput,
      };
    })
  );
  const outputs: MemberRedactedOutput[] = [];
  const missing: TDOId[] = [];
  const workedOnWithoutOutput: TDOId[] = [];
  for (const entry of settled) {
    if (entry.output) {
      outputs.push(entry.output);
    } else {
      missing.push(entry.tdoId);
      if (entry.workedOn) {
        workedOnWithoutOutput.push(entry.tdoId);
      }
    }
  }
  return { outputs, missing, workedOnWithoutOutput };
}

/**
 * Fetch one member's redacted PDF.
 */
export async function serviceFetchPdfBytes(
  signedUri: string
): Promise<ArrayBuffer> {
  const res = await fetch(signedUri);
  if (!res.ok) {
    throw new Error(`merged document: part fetch failed (HTTP ${res.status})`);
  }
  return res.arrayBuffer();
}

/** What a combined asset records about how it was built. */
export interface CombinedDocumentDetails {
  readonly version: number;
  readonly kind: 'document';
  readonly projectTdoId: TDOId;
  /** UTC, full precision — the one timestamp that is comparable. */
  readonly generatedDateTime: string;
  /** Exactly as issued, so a downloaded file can be traced back here. */
  readonly filename: string;
  /**
   * Did this build remove anything? Recorded so a RENAME can reissue the
   * filename under the bracket it was built with, long after the members
   * were read. Absent on older assets, which reissue as `[REDACTED]` — the
   * conservative reading, since `[NO_REDACTIONS]` is the claim.
   */
  readonly hasRedactions?: boolean;
  readonly pageCount: number;
  /**
   * Counters that outlive a single build. `downloads` is cumulative across
   * versions and carried forward on each merge — the question is about the
   * merged document, not the particular build.
   */
  readonly metrics: { readonly downloads: number };
  /** See `CombinedAssetSummary.excluded`. */
  readonly excluded: ReadonlyArray<CombinedExclusion>;
  readonly members: ReadonlyArray<{
    readonly tdoId: TDOId;
    readonly name: string | null;
    /** The EXACT asset consumed, not merely the member it came from. */
    readonly assetId: string;
    readonly version: number;
    readonly pageCount: number;
    readonly startPage: number;
    readonly endPage: number;
    /**
     * This member went in WITHOUT anybody reviewing it (VE-32440).
     *
     * The symmetric fact to `excluded`, and recorded for the same reason
     * that list exists: so a released document can be read back later and
     * say what is in it as well as what is not. The member's own output
     * carries it too, but that asset is rotated on the next burn — this is
     * the copy that survives with the release it describes.
     */
    readonly includedUnreviewed?: boolean;
  }>;
}

interface CreateCombinedAssetResponse {
  createAsset?: {
    id: string;
    createdDateTime: string;
    name: string | null;
    signedUri: string | null;
  };
}

/**
 * Publish the combined PDF on the project TDO.
 */
export async function serviceSaveCombinedDocumentAsset(
  projectTdoId: TDOId,
  pdfBytes: Uint8Array,
  filename: string,
  details: CombinedDocumentDetails,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{ id: string; signedUri: string | null }> {
  const query = `
    mutation saveCombinedDocument($id: ID!, $name: String!, $details: JSONData!) {
      createAsset(input: {
        containerId: $id,
        type: "${MERGED_DOCUMENT_ASSET_TYPE}",
        contentType: "application/pdf",
        name: $name,
        details: $details
      }) {
        id
        createdDateTime
        name
        signedUri
      }
    }
  `;
  const formData = new FormData();
  formData.append('query', query);
  formData.append(
    'variables',
    JSON.stringify({ id: projectTdoId, name: filename, details })
  );
  formData.append('filename', filename);
  formData.append(
    'file',
    new Blob([pdfBytes as BlobPart], { type: 'application/pdf' }),
    filename
  );
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (veritoneAppId) {
    headers['x-veritone-application'] = veritoneAppId;
  }
  const res = await fetch(`${apiRoot}/${graphQLEndpoint}`, {
    method: 'POST',
    body: formData,
    headers,
  });
  if (!res.ok) {
    throw new Error(`merged document save failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: CreateCombinedAssetResponse;
    errors?: Array<{ message?: string }>;
  };
  if (json.errors?.length) {
    throw new Error(
      `merged document save failed: ${json.errors[0]?.message ?? 'unknown error'}`
    );
  }
  const created = json.data?.createAsset;
  if (!created) {
    throw new Error('merged document save returned no asset');
  }
  return { id: created.id, signedUri: created.signedUri };
}

/**
 * The newest combined asset's id and FULL stored details.
 */
export async function serviceCombinedAssetDetails(
  projectTdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{
  assetId: string;
  details: CombinedDocumentDetails;
} | null> {
  const query = `
    query combinedAssetDetails($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${MERGED_DOCUMENT_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records { id details }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject?: {
      assets?: {
        records?: Array<{ id: string; details?: CombinedDocumentDetails }>;
      } | null;
    } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: projectTdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document details lookup failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
  const record = result.data?.temporalDataObject?.assets?.records?.[0];
  if (!record?.details) {
    return null;
  }
  return { assetId: record.id, details: record.details };
}

/**
 * Replace a combined asset's details. `updateAsset` does not merge, so the
 * caller passes the whole record — see `serviceCombinedAssetDetails`.
 */
export async function serviceUpdateCombinedAssetDetails(
  assetId: string,
  details: CombinedDocumentDetails,
  /** Set alongside the details when the document has been renamed. */
  name: string | undefined,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const query = `
    mutation updateCombinedAssetDetails(
      $id: ID!
      $details: JSONData!
      $name: String
    ) {
      updateAsset(input: { id: $id, details: $details, name: $name }) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<{ updateAsset?: { id: string } }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: assetId, details, name },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document details update failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
}

/**
 * Rename the merged document.
 */
export async function serviceRenameProjectTdo(
  projectTdoId: TDOId,
  name: string,
  /**
   * Written in the SAME mutation as the name. They are one decision made on
   * one screen — what this file is called — and splitting them into two
   * writes would let half of it land.
   */
  filenameOptions: MergedFilenameOptions,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const query = `
    mutation renameProject($id: ID!, $name: String!, $details: JSONData!) {
      updateTDO(input: { id: $id, name: $name, details: $details }) {
        id
        name
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    updateTDO?: { id: string; name: string | null } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: {
      id: projectTdoId,
      name,
      details: { mergedFilename: filenameOptions },
    },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document rename failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
  /**
   * A MUTATION THAT RETURNED NOTHING DID NOTHING.
   */
  const updated = result.data?.updateTDO;
  if (!updated?.id) {
    throw new Error(
      'merged document rename failed: the server did not return the updated record'
    );
  }
  /**
   * The echoed name is NOT checked against what we asked for.
   */
}

/**
 * Every combined asset currently on the project, newest first.
 */
export async function serviceListCombinedAssetIds(
  projectTdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string[]> {
  const query = `
    query listCombinedAssets($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${MERGED_DOCUMENT_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 100) {
          records { id }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject?: {
      assets?: { records?: Array<{ id: string }> } | null;
    } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: projectTdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `combined asset list failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
  return (result.data?.temporalDataObject?.assets?.records ?? []).map(
    (record) => record.id
  );
}

/**
 * The next version for a project's combined output.
 */
export async function serviceNextCombinedVersion(
  projectTdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<number> {
  const query = `
    query nextCombinedVersion($id: ID!) {
      temporalDataObject(id: $id) {
        details
        assets(assetType: "${MERGED_DOCUMENT_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 100) {
          records { id details }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject?: {
      details?: { latestCombinedVersion?: number } | null;
      assets?: { records?: Array<{ details?: { version?: number } }> } | null;
    } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: projectTdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document version lookup failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
  const tdo = result.data?.temporalDataObject;
  const records: Array<{ details?: { version?: number } }> =
    tdo?.assets?.records ?? [];
  const assetVersions = records
    .map((record) => record.details?.version)
    .filter((v): v is number => typeof v === 'number' && v > 0);
  // The SHARED parser, not a second reading of the same field. The two had
  // the same rules written out twice, which is one edit away from disagreeing
  // about the next version number on a released document.
  const highest = Math.max(
    0,
    ...assetVersions,
    parseLatestCombinedVersion(tdo?.details)
  );
  return highest + 1;
}

/**
 * Mirror the issued version onto the project TDO.
 */
export async function serviceUpdateLatestCombinedVersion(
  projectTdoId: TDOId,
  version: number,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const query = `
    mutation setLatestCombinedVersion($id: ID!, $details: JSONData!) {
      updateTDO(input: { id: $id, details: $details }) { id }
    }
  `;
  const result = await fetchGraphQLApi<{ updateTDO?: { id: string } }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: {
      id: projectTdoId,
      details: { latestCombinedVersion: version },
    },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document version write failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
}

/**
 * What one lookup of a project tells us.
 */
export interface LatestCombined {
  readonly projectName: string | null;
  readonly asset: CombinedAssetSummary | null;
  /**
   * The filename choices stored on the project. Read in the SAME round trip
   * as the name and the asset, because the combine needs all three at once
   * and they describe one thing.
   */
  readonly filenameOptions: MergedFilenameOptions;
  /** The project's Bates settings, or null when it carries none. */
  readonly batesStamp: ProjectBatesStamp | null;
}

/** The merged document as the editor needs to show it. */
export interface CombinedAssetSummary {
  readonly id: string;
  readonly name: string | null;
  readonly signedUri: string | null;
  readonly createdDateTime: string;
  readonly version: number;
  readonly pageCount: number | null;
  /** Byte size, from the asset's own `fileData`. Null when the server has none. */
  readonly assetSize: number | null;
  /** Cumulative download count, from `details.metrics.downloads`. */
  readonly downloads: number;
  /**
   * What this merged document was BUILT FROM, in order — the members' tdoIds
   * and the exact redacted asset consumed from each.
   */
  /** Mirrors `details.hasRedactions`, for the rename panel's preview. */
  readonly hasRedactions: boolean;
  readonly members: ReadonlyArray<CombinedMemberSummary>;
  /**
   * Members the combine LEFT OUT, and why.
   */
  readonly excluded: ReadonlyArray<CombinedExclusion>;
  /**
   * the merged audit log written alongside this document, or null
   * where the combine predates it or its write failed. Null is a real answer,
   * not an error: the PDF is still releasable, and the download controls offer
   * what exists rather than promising a file that does not.
   */
  readonly mergedAuditLog: {
    readonly id: string;
    readonly name: string | null;
    readonly signedUri: string | null;
  } | null;
}

export interface CombinedExclusion {
  readonly tdoId: TDOId;
  readonly name: string | null;
  /** Only one reason today; named so a second does not need a migration. */
  readonly reason: 'notWorkedOn';
}

/**
 * One member of a merged document, as the Details panel's Contents list and
 * the staleness check read it back.
 */
export interface CombinedMemberSummary {
  readonly tdoId: TDOId;
  /** The EXACT asset consumed, not merely the member it came from. */
  readonly assetId: string;
  readonly name: string | null;
  /** That member's redacted version at the time of the merge. */
  readonly version: number | null;
  /** 1-based, inclusive, into the merged document. Null when not recorded. */
  readonly startPage: number | null;
  readonly endPage: number | null;
}

/**
 * The project's newest merged document, or null if it has never been built.
 */
export async function serviceLatestCombinedAsset(
  projectTdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<LatestCombined> {
  const query = `
    query latestCombinedAsset($id: ID!) {
      temporalDataObject(id: $id) {
        name
        # The filename choices live here, in their own top-level key.
        details
        # The set's NUMBERING, read from the redaction asset's details rather
        # than its body: details comes back with this query, so the stamp and
        # the settings cost nothing extra here, where downloading the body
        # would be a second round trip for a flat object. The body stays
        # authoritative for anyone who has it -- see
        # buildProjectRedactionAssetDetails.
        redactionSettings: assets(assetType: "${PROJECT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records {
            id
            details
          }
        }
        assets(assetType: "${MERGED_DOCUMENT_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records {
            id
            name
            signedUri
            createdDateTime
            details
            # Shown as File Size in the Details panel. Nullable on the server,
            # so absent means unknown rather than empty.
            fileData {
              size
            }
          }
        }
        # the merged audit log, written by the same combine and
        # rotated with it. Fetched HERE rather than on demand so the download
        # controls can offer it without a second round trip at the moment
        # somebody presses a button.
        auditLog: assets(assetType: "${MERGED_AUDIT_LOG_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 5) {
          records {
            id
            name
            signedUri
            # WHICH BUILD IT DESCRIBES. Without this the newest log was taken
            # on trust, and the write is best-effort: when a log write failed,
            # the PREVIOUS build's log survived rotation, was still the newest,
            # and went out beside the new PDF as its chain of custody. A few
            # records rather than one, so the right log is still findable when
            # a later write left a stray behind.
            details
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject?: {
      name?: string | null;
      details?: unknown;
      auditLog?: {
        records?: Array<{
          id: string;
          name: string | null;
          signedUri: string | null;
          details?: { combinedAssetId?: unknown } | null;
        }>;
      } | null;
      redactionSettings?: {
        records?: Array<{ id: string; details?: unknown }> | null;
      } | null;
      assets?: {
        records?: Array<{
          id: string;
          name: string | null;
          signedUri: string | null;
          createdDateTime: string;
          details?: {
            version?: number;
            pageCount?: number;
            hasRedactions?: boolean;
            members?: unknown;
            excluded?: unknown;
            metrics?: { downloads?: number } | null;
          } | null;
          fileData?: { size?: number | null } | null;
        }> | null;
      } | null;
    } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: projectTdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document lookup failed: ${String(result.errors[0]?.message ?? 'unknown error')}`
    );
  }
  const tdo = result.data?.temporalDataObject;
  const projectName = typeof tdo?.name === 'string' ? tdo.name : null;
  const filenameOptions = parseMergedFilenameOptions(tdo?.details);
  // FROM THE ASSET, not the project TDO's details, which no longer carry it.
  // Null settings means the set stamps nothing — a real answer, distinct from
  // the record not having been read, which is why the caller's state is a
  // union rather than a nullable.
  const settingsDetails = parseProjectRedactionAssetDetails(
    tdo?.redactionSettings?.records?.[0]?.details
  );
  const batesStamp: ProjectBatesStamp | null = settingsDetails.bates
    ? {
        modifiedDateTime: settingsDetails.batesModifiedDateTime,
        settings: settingsDetails.bates,
      }
    : null;
  const record = tdo?.assets?.records?.[0];
  if (!record) {
    return { projectName, asset: null, filenameOptions, batesStamp };
  }
  // PAIRED BY ID, not by date. `mergedAuditLog.ts` has always written
  // `details.combinedAssetId` "so a download can pair the two" — and nothing
  // read it, so the pairing it describes never happened. A log that does not
  // name this build is not this build's log, and handing out a chain-of-custody
  // document describing a different file is worse than handing out none: the
  // surfaces fall back to the per-member logs, which are at least true.
  const auditLogRecord = tdo?.auditLog?.records?.find((candidate) => {
    const describes = candidate.details?.combinedAssetId;
    return typeof describes === 'string' && describes === record.id;
  });
  const asset: CombinedAssetSummary = {
    mergedAuditLog: auditLogRecord
      ? {
          id: auditLogRecord.id,
          name: auditLogRecord.name,
          signedUri: auditLogRecord.signedUri,
        }
      : null,
    id: record.id,
    name: record.name,
    signedUri: record.signedUri,
    createdDateTime: record.createdDateTime,
    version:
      typeof record.details?.version === 'number' ? record.details.version : 0,
    pageCount:
      typeof record.details?.pageCount === 'number'
        ? record.details.pageCount
        : null,
    assetSize:
      typeof record.fileData?.size === 'number' ? record.fileData.size : null,
    downloads:
      typeof record.details?.metrics?.downloads === 'number'
        ? record.details.metrics.downloads
        : 0,
    // Absent reads as redacted: `[NO_REDACTIONS]` is the claim, and an asset
    // built before the field recorded nothing either way.
    hasRedactions: record.details?.hasRedactions !== false,
    members: parseCombinedMembers(record.details?.members),
    excluded: parseCombinedExclusions(record.details?.excluded),
  };
  return { projectName, asset, filenameOptions, batesStamp };
}

/**
 * The `excluded` list of a stored combined asset.
 */
function parseCombinedExclusions(
  raw: unknown
): ReadonlyArray<CombinedExclusion> {
  if (!Array.isArray(raw)) {
    return [];
  }
  const parsed: CombinedExclusion[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.tdoId !== 'string' || !record.tdoId) {
      continue;
    }
    parsed.push({
      tdoId: record.tdoId as TDOId,
      name: typeof record.name === 'string' ? record.name : null,
      reason: 'notWorkedOn',
    });
  }
  return parsed;
}

/**
 * The `members` of a stored combined asset, reduced to what staleness needs.
 */
function parseCombinedMembers(
  raw: unknown
): ReadonlyArray<CombinedMemberSummary> {
  if (!Array.isArray(raw)) {
    return [];
  }
  const num = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;
  const parsed: CombinedMemberSummary[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const { tdoId, assetId } = record;
    // An entry without BOTH ids cannot take part in the staleness comparison,
    // so it is dropped rather than coerced: a half-built entry would make a
    // merged document look current against a member it has no real record of.
    // The descriptive fields below are allowed to be missing — the Contents
    // list says "unknown" for a page range, which is honest.
    if (typeof tdoId !== 'string' || !tdoId || typeof assetId !== 'string') {
      continue;
    }
    parsed.push({
      tdoId: tdoId as TDOId,
      assetId,
      name: typeof record.name === 'string' ? record.name : null,
      version: num(record.version),
      startPage: num(record.startPage),
      endPage: num(record.endPage),
    });
  }
  return parsed;
}

/**
 * Minimum shape of a request document needed to judge a combined document's
 * currency: its id, and the output assets the case-contents query rides along.
 */
export interface CombineStalenessFile {
  readonly id: unknown;
  readonly redactedMediaAssets?: {
    readonly records?: ReadonlyArray<{ readonly id: string }>;
  };
}

/**
 * Members whose own redacted output no longer matches their saved redactions.
 */
export type StaleMemberIds = ReadonlySet<string>;

/** Has anything changed since the combined document was built? */
/** `'unknown'` is the answer that used to be missing; its absence was a
 * release hazard. */
export type CombinedStaleVerdict = 'current' | 'stale' | 'unknown';

export const combinedStaleVerdict = (
  combined: CombinedAssetSummary | null | undefined,
  memberIds: readonly string[] | null | undefined,
  documents: readonly CombineStalenessFile[],
  staleMemberIds?: StaleMemberIds,
  /**
   * Members the combine is allowed to leave out — files nobody has opened.
   * Removed from the comparison, or a merged document is permanently stale
   * for containing exactly what it was asked to contain.
   */
  excludableMemberIds?: ReadonlySet<string>
): CombinedStaleVerdict => {
  const recorded = combined?.members;
  // Nothing recorded is answerable: there is no merged document for anything
  // to be stale about.
  if (!recorded?.length) {
    return 'current';
  }
  if (!memberIds) {
    return 'unknown';
  }
  /**
   * A MEMBER NO LONGER IN THE REQUEST IS NOT COMPARABLE EITHER.
   */
  const present = documents.length
    ? new Set(documents.map((file) => String(file.id)))
    : null;
  const comparable = memberIds.filter(
    (id) =>
      !excludableMemberIds?.has(id) && (!present || present.has(String(id)))
  );
  if (recorded.length !== comparable.length) {
    return 'stale';
  }
  // Checked before the rest and against the CURRENT membership, not the
  // recorded one: a member whose output is behind its own saves makes the
  // merged document wrong whether or not anything about the merge changed.
  if (staleMemberIds?.size && comparable.some((id) => staleMemberIds.has(id))) {
    return 'stale';
  }
  const newestRedactedById = new Map(
    documents.map((file) => [
      String(file.id),
      file.redactedMediaAssets?.records?.[0]?.id,
    ])
  );
  const moved = comparable.some((tdoId, index) => {
    const entry = recorded[index];
    if (!entry || String(entry.tdoId) !== tdoId) {
      return true;
    }
    const newest = newestRedactedById.get(tdoId);
    // An absent newest means this file's assets have not arrived, not that it
    // has lost its output — say "unchanged" rather than warn at a reviewer
    // while the list is still filling in.
    return !!newest && newest !== entry.assetId;
  });
  return moved ? 'stale' : 'current';
};

/**
 * The verdict as a boolean, for the surfaces that only act on a definite
 * "stale" — the panel's Update offer and the editor's banner. An unknown
 * verdict reads as "not stale" here on purpose: those surfaces OFFER work
 * rather than gate a release, and offering a rebuild on the strength of a
 * failed read would be inventing a problem.
 */
export const isCombinedStale = (
  ...args: Parameters<typeof combinedStaleVerdict>
): boolean => combinedStaleVerdict(...args) === 'stale';
